import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack } from "@mui/material";
import PhotoUrlFactory from "../../../../../infrastructure/url-factory/PhotoUrlFactory";
import { CopyTextToClipboard } from "../../../../../action/copyTextToClipboard";
import { NumberishId } from "../../../../../model/type";
import Filters from "../common/Filters";
import MediumsDataTable from "./MediumsDataTable";
import useTable from "../../../../hooks/useTable";
import { GetOfferMediums } from "../../../../../action/getOfferMediums";
import useTrackPromise from "../../../../hooks/useTrackPromise";
import { OfferCreatorFilterParams } from "../../../../../model/filter-params/OfferCreatorFilterParams";
import { UpdateManyAvailabilities } from "../../../../../action/updateManyAvailabilities";
import getOfferId from "../../../../../core/getOfferId";
import { ActionButton } from "../../../../components/Button";
import PriceEditionDialog from "./PriceEditionDialog";
import ProductionPriceEditionDialog from "./ProductionPriceEditionDialog";
import { GetMediumFilterOptions } from "../../../../../action/getMediumFilterOptions";
import { GetAllMediumIds } from "../../../../../action/getAllMediumIds";
import Dialog from "../../../../components/Dialog";

const MediumsList: React.FC<MediumsListProps> = props => {

    const offerId = getOfferId();

    const {
        dataQueryParams,
        onCheckAllClick,
        onUncheckAllClick,
        onRowClick,
        onRowShiftClick,
        onFilterChange,
        onClearFiltersClick,
        sortableHeaderPropsFactory,
        selectedIds,
        paginationProps,
        setPagination,
        isAnyFilterActive,
    } = useTable<OfferCreatorFilterParams>();

    const [_getOfferMediums, paginatedOfferMediums, statusGetOfferMediums] = useTrackPromise(props.getOfferMediums);
    const getOfferMediums = useCallback(
        () => _getOfferMediums(offerId, dataQueryParams)
            .then(({ pagination }) => {
                setPagination(pagination);
            }),
        [dataQueryParams, offerId]
    );

    const [getMediumFilterOptions, mediumFilterOptions] = useTrackPromise(props.getMediumFilterOptions);
    const [getAllMediumIds, , statusGetAllMediums] = useTrackPromise(props.getAllMediumIds);

    const allDisplayedMediumIds = useMemo(
        () => paginatedOfferMediums?.records.map(medium => medium.mediumId) || [],
        [paginatedOfferMediums?.records]
    );

    const [priceDialogOpen, setPriceDialogOpen] = useState(false);
    const [productionPriceDialogOpen, setProductionPriceDialogOpen] = useState(false);
    const [allMediumIds, setAllMediumIds] = useState<NumberishId[]>([]);
    const [checkAllDialogOpen, setCheckAllDialogOpen] = React.useState(false);

    const checkAll = useCallback((checkOnlyDisplayed: boolean) => {
        if (checkOnlyDisplayed || allDisplayedMediumIds.length === allMediumIds.length) {
            onCheckAllClick(allDisplayedMediumIds);
            setCheckAllDialogOpen(false);
        } else {
            getAllMediumIds(offerId).then(ids => {
                onCheckAllClick(ids);
            }).then(() => setCheckAllDialogOpen(false));
        }
    }, [onCheckAllClick, paginatedOfferMediums?.records]);

    const handleOnCheckAllClick = useCallback(() => {
        if (allDisplayedMediumIds.length === allMediumIds.length) {
            onCheckAllClick(allDisplayedMediumIds);
            return;
        }
        setCheckAllDialogOpen(true);
    }, [onCheckAllClick, paginatedOfferMediums?.records]);

    const rowClick = useCallback((availabilityId: NumberishId, e: MouseEvent) => {
        if (e.shiftKey) {
            onRowShiftClick(availabilityId, allDisplayedMediumIds);
            return;
        }
        onRowClick(availabilityId);
    }, [onRowClick, onRowShiftClick, allDisplayedMediumIds]);


    useEffect(() => {
        props.getAllMediumIds(offerId).then(setAllMediumIds);
    }, [dataQueryParams])

    useEffect(() => {
        getOfferMediums();
    }, [dataQueryParams])

    useEffect(() => {
        getMediumFilterOptions(offerId);
    }, [])

    return (
        <>
            <Stack spacing={ 2 }>
                <Stack spacing={ 2 } direction='row'>
                    <Box maxWidth='400px' pl={ 2 } pr={ 2 }>
                        <ActionButton
                            onClick={ () => setPriceDialogOpen(true) }
                            disabled={ selectedIds.length === 0 }
                            sx={ { whiteSpace: 'nowrap' } }
                        >
                            Edytuj cenę dla zaznaczonych nośników ({ selectedIds.length })
                        </ActionButton>
                    </Box>
                    <Box maxWidth='500px' pl={ 2 } pr={ 2 }>
                        <ActionButton
                            onClick={ () => setProductionPriceDialogOpen(true) }
                            disabled={ selectedIds.length === 0 }
                            sx={ { whiteSpace: 'nowrap' } }
                        >
                            Edytuj cenę za druk + montaż dla zaznaczonych nośników ({ selectedIds.length })
                        </ActionButton>
                    </Box>
                </Stack>
                <Filters
                    values={ dataQueryParams.filters! }
                    onFilterChange={ onFilterChange }
                    filterOptions={ mediumFilterOptions || {} }
                    onClearFiltersClick={ onClearFiltersClick }
                    isAnyFilterActive={ isAnyFilterActive }
                />

                <MediumsDataTable
                    onCheckAllClick={ handleOnCheckAllClick }
                    onUncheckAllClick={ onUncheckAllClick }
                    onRowClick={ rowClick }
                    photoUrlFactory={ props.photoUrlFactory }
                    copyTextToClipboard={ props.copyTextToClipboard }
                    context={ {
                        sortableHeaderPropsFactory,
                        paginatedOfferMediums,
                        selectedIds,
                        statusGetOfferMediums,
                        paginationProps
                    } }
                    updateManyAvailabilities={ props.updateManyAvailabilities }
                    onEdit={ getOfferMediums }
                />
            </Stack>
            {
                priceDialogOpen &&
                <PriceEditionDialog
                    onClose={ () => setPriceDialogOpen(false) }
                    onSubmit={
                        values => props.updateManyAvailabilities(
                            offerId,
                            selectedIds,
                            [],
                            values
                        ).then(() => {
                            getOfferMediums();
                            setPriceDialogOpen(false);
                        })
                    }
                />
            }
            {
                productionPriceDialogOpen &&
                <ProductionPriceEditionDialog
                    onClose={ () => setProductionPriceDialogOpen(false) }
                    onSubmit={
                        values => props.updateManyAvailabilities(
                            offerId,
                            selectedIds,
                            [],
                            values
                        ).then(() => {
                            getOfferMediums();
                            setProductionPriceDialogOpen(false);
                        })
                    }
                />
            }
            {
                checkAllDialogOpen &&
                <Dialog
                    open={ true }
                    onCloseClick={ () => setCheckAllDialogOpen(false) }
                >
                    <Stack p={ 4 } spacing={ 5 } width='600px'>
                        <Box>
                            Czy chcesz zaznaczyć wszystkie nośniki <b>({ allMediumIds.length })</b> w ofercie,
                            <br/>
                            czy tylko te widoczne na liście <b>({ allDisplayedMediumIds.length })</b>?
                        </Box>
                        <Box display='flex' justifyContent='space-between'>
                            <ActionButton onClick={ () => checkAll(false) } loading={ statusGetAllMediums.pending() }>
                                Zaznacz wszystkie
                            </ActionButton>
                            <ActionButton onClick={ () => checkAll(true) } loading={ statusGetAllMediums.pending() }>
                                Zaznacz tylko widoczne na liście
                            </ActionButton>
                        </Box>

                    </Stack>
                </Dialog>
            }
        </>
    );
}
export default MediumsList;

export interface MediumsListProps {
    readonly photoUrlFactory: PhotoUrlFactory;
    readonly copyTextToClipboard: CopyTextToClipboard;
    readonly getOfferMediums: GetOfferMediums<true>;
    readonly updateManyAvailabilities: UpdateManyAvailabilities;
    readonly getMediumFilterOptions: GetMediumFilterOptions;
    readonly getAllMediumIds: GetAllMediumIds<true>;
}